import React, { useState, useEffect } from "react";
import {
  Outlet, useNavigate, useLocation, Link
} from "react-router-dom";
import {
  AppBar, Toolbar, Typography, Drawer, List, ListItemButton,
  ListItemText, Box, CssBaseline, FormControlLabel, Switch,
  Button, Snackbar, Alert, Slide, IconButton, useMediaQuery,
  Menu, MenuItem
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "./AuthContext";

const drawerWidth = 240;

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const routes = {
    "Dashboard": "/dashboard",
    "Compare Companies": "/compare-companies",
    "Favorites": "/favorites",
    "Market Overview": "/market-overview",
    "Valuation Models": "/valuation-models",
    "Reports": "/reports",
    "Education": "/education",
    "Model Portfolios": "/portfolios",
    "Company Spotlight": "/company-spotlight"
  };

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem("subscribePopupShown");

    const isExcludedRoute =
      location.pathname.startsWith("/auth") || location.pathname.startsWith("/subscribe");

    if (!user && !alreadyShown && !isExcludedRoute) {
      const timer = setTimeout(() => {
        setShowPopup(true);
        sessionStorage.setItem("subscribePopupShown", "true");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [user, location]);

  const handleClosePopup = () => setShowPopup(false);

  const drawer = (
    <div>
      <Toolbar />
      <List>
        {Object.entries(routes).map(([label, path]) => (
          <ListItemButton key={label} onClick={() => {
            navigate(path);
            setMobileOpen(false);
          }}>
            <ListItemText primary={label} />
          </ListItemButton>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar position="fixed" sx={{ zIndex: 1300 }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Logo and Menu Button */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileOpen(!mobileOpen)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/home" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography variant="h6" sx={{ cursor: 'pointer' }}>
                Financial Dashboard
              </Typography>
            </Link>
          </Box>

          {/* Right Side Controls */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={<Switch checked={darkMode} onChange={() => setDarkMode(!darkMode)} />}
              label="Dark Mode"
              sx={{ mr: 2 }}
            />

            {!user ? (
              <Button color="inherit" onClick={() => navigate("/auth")}>
                Sign Up
              </Button>
            ) : (
              <>
                <IconButton color="inherit" onClick={handleMenuOpen}>
                  <AccountCircle />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => { handleMenuClose(); navigate("/profile"); }}>
                    Profile
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate("/contact"); }}>
                    Contact Us
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate("/subscribe"); }}>
                    Subscription
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleMenuClose();
                    // TODO: Replace with actual logout logic
                    navigate("/auth");
                  }}>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: "border-box" }
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ [`& .MuiDrawer-paper`]: { width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Outlet />
      </Box>

      {/* Subscribe Popup */}
      <Snackbar
        open={showPopup}
        onClose={handleClosePopup}
        autoHideDuration={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={handleClosePopup}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                handleClosePopup();
                navigate("/auth");
              }}
            >
              Sign Up
            </Button>
          }
        >
          🚀 Unlock full dashboard access by creating a free account!
        </Alert>
      </Snackbar>
    </Box>
  );
}
